import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../i18n";
import { SEO_PAGES } from "../seoPages";

export const SITE_URL = "https://da-creations.com";

// Upsert (never duplicate) head tags — updates the static tags shipped in
// public/index.html in place, so non-JS crawlers keep the static floor and
// JS-rendering crawlers see the per-route values. One tag, always current.
function setMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const STATIC_META = {
  "/": {
    title: {
      en: "Jewellery Casting Atelier in Urbino, Italy | DA Creations",
      it: "Atelier di Fusione Orafa a Urbino, Italia | DA Creations",
    },
    description: {
      en: "Artisan jewellery casting atelier in Urbino, Italy. Lost-wax casting, gemstone setting, engraving, CAD and 3D prototyping — priced at live metal spot rates.",
      it: "Atelier artigianale di fusione orafa a Urbino, Italia. Cera persa, incastonatura, incisione, CAD e prototipi 3D — alla quotazione live del metallo.",
    },
    canonical: "/",
  },
  "/contact": {
    title: {
      en: "Start a Project — Contact the Atelier | DA Creations",
      it: "Inizia un Progetto — Contatta l'Atelier | DA Creations",
    },
    description: {
      en: "Start your jewellery project with DA Creations Atelier: send a brief, sketches or CAD files. We reply within 48 hours, in English or Italian.",
      it: "Avvia il tuo progetto di gioielleria con DA Creations Atelier: invia brief, bozzetti o file CAD. Rispondiamo entro 48 ore, in italiano o inglese.",
    },
    canonical: "/contact",
  },
};

const PRIVATE_ROUTES = ["/login", "/portal", "/team", "/forgot-password", "/reset-password"];

// Route-aware <head> manager. Rendered once inside the router; updates title,
// description, canonical and social tags per route, in the active language.
export default function RouteSeo() {
  const { pathname } = useLocation();
  const { lang, L } = useLang();
  const key = pathname.replace(/\/+$/, "") || "/";

  useEffect(() => {
    const seoSlug = Object.keys(SEO_PAGES).find((s) => `/${s}` === key);
    let title, description, canonical, robots;

    if (seoSlug) {
      const page = SEO_PAGES[seoSlug];
      title = page.meta[lang]?.title || page.meta.en.title;
      description = page.meta[lang]?.description || page.meta.en.description;
      canonical = page.path;
    } else if (STATIC_META[key]) {
      title = L(STATIC_META[key].title);
      description = L(STATIC_META[key].description);
      canonical = STATIC_META[key].canonical;
    } else if (PRIVATE_ROUTES.includes(key)) {
      title = "DA Creations Atelier";
      robots = "noindex, follow";
    } else {
      title = L(STATIC_META["/"].title);
      description = L(STATIC_META["/"].description);
      canonical = STATIC_META["/"].canonical;
    }

    document.title = title;
    if (description) setMeta("name", "description", description);
    setMeta("name", "robots", robots || "index, follow, max-image-preview:large, max-snippet:-1");
    if (canonical) setCanonical(`${SITE_URL}${canonical}`);
    setMeta("property", "og:title", title);
    if (description) setMeta("property", "og:description", description);
    if (canonical) setMeta("property", "og:url", `${SITE_URL}${canonical}`);
    setMeta("property", "og:locale", lang === "it" ? "it_IT" : "en_US");
    setMeta("name", "twitter:title", title);
    if (description) setMeta("name", "twitter:description", description);
  }, [key, lang, L]);

  return null;
}

// Injects a route-scoped JSON-LD block (e.g. FAQPage) into <head>, removed on unmount.
export function JsonLd({ id, data }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = `jsonld-${id}`;
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => el.remove();
  }, [id, data]);
  return null;
}
